export interface FinderFilterState {
  search: string;
  minPrice: string;
  maxPrice: string;
  minBedrooms: number;
  minBathrooms: number;
  city: string;
}

export const EMPTY_FILTERS: FinderFilterState = {
  search: '',
  minPrice: '',
  maxPrice: '',
  minBedrooms: 0,
  minBathrooms: 0,
  city: '',
};

export function isFilterActive(filters: FinderFilterState): boolean {
  return (
    filters.search !== '' ||
    filters.minPrice !== '' ||
    filters.maxPrice !== '' ||
    filters.minBedrooms > 0 ||
    filters.minBathrooms > 0 ||
    filters.city !== ''
  );
}

const BEDROOM_OPTIONS = [1, 2, 3, 4];
const BATHROOM_OPTIONS = [1, 1.5, 2, 3];

/**
 * The finder's instrument panel — every control changes the results and the
 * map instantly, client-side (see RentalFinder.tsx). No "Search" button:
 * at this company's scale (10-30 listings, already fetched in full) there
 * is nothing to wait for, so a submit step would only add a click.
 */
export function RentalFinderFilters({
  filters,
  onChange,
  cities,
  resultCount,
}: {
  filters: FinderFilterState;
  onChange: (filters: FinderFilterState) => void;
  cities: string[];
  resultCount: number;
}) {
  function set<K extends keyof FinderFilterState>(key: K, value: FinderFilterState[K]) {
    onChange({ ...filters, [key]: value });
  }

  return (
    <div className="public__finder-toolbar">
      <div className="public__finder-toolbar-top">
        <h1>Rental Finder</h1>
        <p className="public__finder-count">
          {resultCount} {resultCount === 1 ? 'rental matches' : 'rentals match'} your search
        </p>
      </div>

      <div className="public__finder-controls">
        <label className="public__finder-field public__finder-field--search">
          <span>Search</span>
          <input
            type="text"
            className="public__finder-search"
            placeholder="Address, city, or keyword"
            value={filters.search}
            onChange={(event) => set('search', event.target.value)}
          />
        </label>

        <label className="public__finder-field">
          <span>Min rent</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="No min"
            value={filters.minPrice}
            onChange={(event) => set('minPrice', event.target.value)}
          />
        </label>

        <label className="public__finder-field">
          <span>Max rent</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="No max"
            value={filters.maxPrice}
            onChange={(event) => set('maxPrice', event.target.value)}
          />
        </label>

        <label className="public__finder-field">
          <span>Bedrooms</span>
          <select
            value={filters.minBedrooms}
            onChange={(event) => set('minBedrooms', Number(event.target.value))}
          >
            <option value={0}>Any</option>
            {BEDROOM_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}+
              </option>
            ))}
          </select>
        </label>

        <label className="public__finder-field">
          <span>Bathrooms</span>
          <select
            value={filters.minBathrooms}
            onChange={(event) => set('minBathrooms', Number(event.target.value))}
          >
            <option value={0}>Any</option>
            {BATHROOM_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}+
              </option>
            ))}
          </select>
        </label>

        <label className="public__finder-field">
          <span>City</span>
          <select value={filters.city} onChange={(event) => set('city', event.target.value)}>
            <option value="">Any city</option>
            {cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </label>

        {isFilterActive(filters) ? (
          <button
            type="button"
            className="public__finder-reset"
            onClick={() => onChange(EMPTY_FILTERS)}
          >
            Reset filters
          </button>
        ) : null}
      </div>
    </div>
  );
}
